import type { ButtonHTMLAttributes, ReactNode } from "react"
import { Spinner } from "./Spinner.js"

type Variant = "primary" | "secondary" | "ghost" | "destructive"
type Size = "sm" | "md" | "lg"

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: Variant
  size?: Size
  /** Shows a spinner and disables the button. Callers pass this from
   *  whatever async state they're tracking (mutation in flight, etc) —
   *  every action in the app must show this instead of appearing inert
   *  while it works (design principle: "siempre mostrar el estado
   *  actual de una acción"). */
  loading?: boolean
  children: ReactNode
}

const VARIANT_STYLES: Record<Variant, React.CSSProperties> = {
  primary: {
    background: "var(--brand-primary)",
    color: "var(--text-on-brand)",
    border: "1px solid transparent",
  },
  secondary: {
    background: "var(--bg-surface)",
    color: "var(--text-primary)",
    border: "1px solid var(--border-default)",
  },
  ghost: {
    background: "transparent",
    color: "var(--brand-primary)",
    border: "1px solid transparent",
  },
  destructive: {
    background: "var(--status-error)",
    color: "var(--text-on-brand)",
    border: "1px solid transparent",
  },
}

const SIZE_STYLES: Record<Size, React.CSSProperties> = {
  sm: { padding: "6px 12px", fontSize: "var(--text-sm)", borderRadius: "var(--radius-sm)" },
  md: { padding: "10px 16px", fontSize: "var(--text-base)", borderRadius: "var(--radius-md)" },
  lg: { padding: "14px 24px", fontSize: "var(--text-lg)", borderRadius: "var(--radius-md)" },
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  style,
  children,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading

  return (
    <button
      {...rest}
      disabled={isDisabled}
      aria-busy={loading}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--space-2)",
        fontWeight: "var(--font-weight-semibold)",
        lineHeight: "var(--line-height-ui)",
        transition: `background-color var(--duration-small) var(--ease-out), transform var(--duration-micro) var(--ease-out), opacity var(--duration-small) var(--ease-out)`,
        opacity: isDisabled ? 0.6 : 1,
        cursor: isDisabled ? "not-allowed" : "pointer",
        ...VARIANT_STYLES[variant],
        ...SIZE_STYLES[size],
        ...style,
      }}
      onMouseDown={(e) => {
        if (!isDisabled) e.currentTarget.style.transform = "scale(0.97)"
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "scale(1)"
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)"
      }}
    >
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  )
}
